import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  AlertTriangle,
  Clock,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Users,
  Building2,
  Mail
} from 'lucide-react';
import { submitRoleRequest, getPendingRoleRequests, getApprovedRoleRequests, updateRoleRequestStatus, getUsers, getOrganizationalUnits, getOUApprovers, getPrimaryOUs } from '../firebase';
import { useUser } from '../contexts/UserContext';

const RoleDelegation = () => {
  // Get current user context
  const { user } = useUser();
  // Simplified state for role request form
  const [showRoleRequestForm, setShowRoleRequestForm] = useState(false);
  const [roleRequest, setRoleRequest] = useState({
    requestedFor: '',
    requestedBy: '',
    roleType: '',
    organisationalUnit: '',
    reason: '',
    urgent: false
  });
  const [submitting, setSubmitting] = useState(false);
  
  // State for pending role requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [showApprovedList, setShowApprovedList] = useState(false);
  const [loading, setLoading] = useState(true);

  // State for form data (users, OUs, approvers)
  const [allUsers, setAllUsers] = useState([]);
  const [organisationalUnits, setOrganisationalUnits] = useState([]);
  const [primaryOUs, setPrimaryOUs] = useState([]);
  const [ouApprovers, setOuApprovers] = useState([]);
  const [queueInfo, setQueueInfo] = useState(null);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [loadingApprovers, setLoadingApprovers] = useState(false);

  // Load pending role requests on component mount
  useEffect(() => {
    fetchPendingRoleRequests();
  }, []);

  // Auto-populate requester and fetch form data when form opens
  useEffect(() => {
    if (showRoleRequestForm && user?.email) {
      // Reset form when opening
      setRoleRequest({
        requestedFor: '',
        requestedBy: user.email,
        roleType: '',
        organisationalUnit: '',
        reason: '',
        urgent: false
      });
      fetchFormData();
    }
  }, [showRoleRequestForm, user]);

  const fetchFormData = async () => {
    setLoadingFormData(true);
    try {
      // Fetch users, OUs, and Primary OUs in parallel
      const [usersResponse, ouResponse, primaryOUsResponse] = await Promise.all([
        getUsers(),
        getOrganizationalUnits(),
        getPrimaryOUs()
      ]);

      if (usersResponse.data?.success) {
        setAllUsers(usersResponse.data.data || []);
      }

      if (ouResponse.data?.success) {
        setOrganisationalUnits(ouResponse.data.data || []);
      }

      if (primaryOUsResponse.data?.success) {
        const primaryOUsData = primaryOUsResponse.data.data || [];
        setPrimaryOUs(primaryOUsData);
        
        // Set default OU to first Primary OU (customer root OU) if available and not already set
        // This pre-selects the customer root OU, but user can still change it
        if (primaryOUsData.length > 0) {
          const currentOU = roleRequest.organisationalUnit;
          const defaultOU = primaryOUsData[0];
          const defaultOUPath = defaultOU.queuePath || defaultOU.queueId;
          
          // Only set default if no OU is currently selected
          if (!currentOU) {
            setRoleRequest(prev => ({ ...prev, organisationalUnit: defaultOUPath }));
            
            // Automatically fetch approvers for the default OU
            // Fetch approvers directly using the OU data we just received
            (async () => {
              try {
                setLoadingApprovers(true);
                const response = await getOUApprovers({
                  ouPath: defaultOU.queuePath,
                  ouId: defaultOU.queueId
                });
                
                if (response.data?.success) {
                  setOuApprovers(response.data.data.approvers || []);
                  setQueueInfo({
                    queueId: response.data.data.queueId,
                    queueName: response.data.data.queueName,
                    queuePath: response.data.data.queuePath
                  });
                }
              } catch (error) {
                console.error('Error fetching default OU approvers:', error);
              } finally {
                setLoadingApprovers(false);
              }
            })();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
    } finally {
      setLoadingFormData(false);
    }
  };

  // Fetch OU approvers when OU is selected
  const handleOUChange = async (selectedOU) => {
    console.log('handleOUChange called with:', selectedOU);
    setRoleRequest(prev => {
      const updated = { ...prev, organisationalUnit: selectedOU };
      console.log('Updated roleRequest state:', updated);
      return updated;
    });
    
    if (selectedOU) {
      try {
        setLoadingApprovers(true);
        // Check if it's a Primary OU first
        const primaryOU = primaryOUs.find(ou => ou.queuePath === selectedOU || ou.queueId === selectedOU);
        let ou = primaryOU;
        
        // If not a Primary OU, check regular OUs
        if (!ou) {
          ou = organisationalUnits.find(ou => ou.path === selectedOU || ou.id === selectedOU);
        }
        
        if (ou) {
          // For Primary OUs, use queuePath and queueId; for regular OUs, use path and id
          const ouPath = primaryOU ? primaryOU.queuePath : ou.path;
          const ouId = primaryOU ? primaryOU.queueId : ou.id;
          
          // Fetch approvers for this OU
          const response = await getOUApprovers({
            ouPath: ouPath,
            ouId: ouId
          });
          
          if (response.data?.success) {
            setOuApprovers(response.data.data.approvers || []);
            setQueueInfo({
              queueId: response.data.data.queueId,
              queueName: response.data.data.queueName,
              queuePath: response.data.data.queuePath
            });
          }
        }
      } catch (error) {
        console.error('Error fetching OU approvers:', error);
        setOuApprovers([]);
        setQueueInfo(null);
      } finally {
        setLoadingApprovers(false);
      }
    } else {
      setOuApprovers([]);
      setQueueInfo(null);
    }
  };

  const fetchPendingRoleRequests = async () => {
    try {
      setLoading(true);
      console.log('Fetching role requests...');
      const response = await getPendingRoleRequests();
      console.log('Role requests response:', response);
      if (response.data?.success && response.data.data) {
        console.log('Setting role requests:', response.data.data);
        setPendingRequests(response.data.data);
      } else {
        console.log('No role requests found or response failed');
      }
    } catch (error) {
      console.error('Error fetching role requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely format Firestore Timestamps
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // Handle Firestore Timestamp with toDate() method
      if (dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleString();
      }
      
      // Handle Firestore Timestamp with seconds property
      if (dateValue && typeof dateValue.seconds === 'number') {
        return new Date(dateValue.seconds * 1000).toLocaleString();
      }
      
      // Handle ISO string or regular date
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return 'N/A';
    }
  };

  const fetchApprovedRoleRequests = async () => {
    if (approvedRequests.length > 0) {
      // Already loaded, just toggle visibility
      setShowApprovedList(!showApprovedList);
      return;
    }

    setLoadingApproved(true);
    try {
      console.log('Fetching approved role requests...');
      const response = await getApprovedRoleRequests();
      console.log('Approved role requests response:', response);
      if (response.data?.success && response.data.data) {
        console.log('Setting approved role requests:', response.data.data);
        setApprovedRequests(response.data.data);
        setShowApprovedList(true);
      } else {
        console.log('No approved role requests found or response failed');
        setApprovedRequests([]);
        setShowApprovedList(true);
      }
    } catch (error) {
      console.error('Error fetching approved role requests:', error);
      setApprovedRequests([]);
    } finally {
      setLoadingApproved(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    try {
      await updateRoleRequestStatus({
        requestId: requestId,
        status: newStatus
      });
      
      // Refresh the pending requests list
      await fetchPendingRoleRequests();
      
      // If approved/completed, also refresh approved list if it's open
      if (showApprovedList && (newStatus === 'approved' || newStatus === 'completed')) {
        setApprovedRequests([]); // Clear to force refresh
        await fetchApprovedRoleRequests();
      }
      
      alert(`Role request status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating role request status:', error);
      alert('Failed to update role request status');
    }
  };

  // Simple form handlers
  const handleRoleRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate required fields before submission
      const orgUnitValue = roleRequest.organisationalUnit;
      console.log('Pre-submission validation - organisationalUnit:', orgUnitValue);
      console.log('Type:', typeof orgUnitValue);
      console.log('Is truthy:', !!orgUnitValue);
      console.log('Is empty string:', orgUnitValue === '');
      
      if (!orgUnitValue || (typeof orgUnitValue === 'string' && orgUnitValue.trim() === '')) {
        alert('Please select an Organisational Unit');
        setSubmitting(false);
        return;
      }
      
      console.log('Submitting role request:', roleRequest);
      console.log('Organisational Unit value:', orgUnitValue);
      
      // Ensure we're sending the correct field name
      const submissionData = {
        ...roleRequest,
        organisationalUnit: orgUnitValue
      };
      console.log('Final submission data:', submissionData);
      
      const response = await submitRoleRequest(submissionData);
      
      if (response.data?.success) {
        console.log('Role request submitted successfully:', response.data);
        alert('Role request submitted successfully! An administrator will review your request and you will receive an email confirmation.');
        setShowRoleRequestForm(false);
        setRoleRequest({
          requestedFor: '',
          requestedBy: '',
          roleType: '',
          organisationalUnit: '',
          reason: '',
          urgent: false
        });
        // Refresh the pending requests list
        await fetchPendingRoleRequests();
      } else {
        throw new Error(response.data?.message || 'Failed to submit role request');
      }
    } catch (error) {
      console.error('Error submitting role request:', error);
      alert(`Failed to submit role request: ${error.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Available role types for the form
  const roleTypes = [
    { value: 'device_support', label: 'Device Support', description: 'Manage Chrome devices and provide user support' },
    { value: 'ou_management', label: 'OU Management', description: 'Create and manage organisational units' },
    { value: 'user_management', label: 'User Management', description: 'Create, edit, and delete users' },
    { value: 'custom', label: 'Custom Role', description: 'Specify a custom role requirement' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Role Request</h1>
          <p className="text-gray-600 mt-2">Request administrative roles for users in your organisation</p>
        </div>
        <button
          onClick={() => setShowRoleRequestForm(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={16} />
          <span>Request Role</span>
        </button>
      </div>

      {/* Information Card */}
      <div className="card">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Role Management</h3>
            <p className="text-gray-600 mt-2">
              Direct role assignment must be performed by Global administrator for security reasons. 
              Please use the role request form below to request administrative roles for users.
              An administrator will review and process your request.
            </p>
          </div>
        </div>
      </div>

      {/* Approved Role Requests - Collapsible */}
      <div className="bg-white rounded-lg shadow mb-8">
        <button
          onClick={fetchApprovedRoleRequests}
          className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
            Approved Role Requests
            {approvedRequests.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({approvedRequests.length})
              </span>
            )}
          </h2>
          {loadingApproved ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
          ) : (
            showApprovedList ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )
          )}
        </button>
        
        {showApprovedList && approvedRequests.length > 0 && (
          <div className="divide-y divide-gray-200">
            {approvedRequests.map((req) => (
              <div key={req.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {req.requestedFor}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        req.status === 'approved' 
                          ? 'bg-green-100 text-green-800'
                          : req.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : req.status === 'active'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {req.status === 'approved' ? 'Approved' : 
                         req.status === 'completed' ? 'Completed' :
                         req.status === 'active' ? 'Active' : req.status}
                      </span>
                      {req.urgent && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Urgent
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Requested By:</strong> {req.requestedBy || req.requestedByEmail}</p>
                        <p><strong>Role Type:</strong> {req.roleType}</p>
                        <p><strong>Organisational Unit:</strong> {req.organisationalUnit}</p>
                      </div>
                      <div>
                        <p><strong>Requested At:</strong> {formatDate(req.requestedAt)}</p>
                        {req.updatedAt && (
                          <p><strong>Updated:</strong> {formatDate(req.updatedAt)}</p>
                        )}
                        {req.updatedBy && (
                          <p><strong>Last Updated By:</strong> {req.updatedBy}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Reason:</strong> {req.reason}
                    </p>
                    {req.notes && (
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Notes:</strong> {req.notes}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 text-sm text-green-600">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {req.status === 'approved' ? 'Approved' : 
                       req.status === 'completed' ? 'Completed' : 'Active'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {showApprovedList && approvedRequests.length === 0 && !loadingApproved && (
          <div className="p-6 text-center text-gray-500">
            <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p>No approved role requests found</p>
          </div>
        )}
      </div>

      {/* Pending Role Requests */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" />
          <span className="ml-3 text-gray-600">Loading role requests...</span>
        </div>
      ) : pendingRequests.length > 0 ? (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Clock className="h-6 w-6 text-yellow-600 mr-2" />
              Pending Role Requests ({pendingRequests.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingRequests.map((req) => (
              <div key={req.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {req.requestedFor}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        req.status === 'approved' 
                          ? 'bg-green-100 text-green-800'
                          : req.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : req.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {req.status === 'approved' ? 'Approved' : 
                         req.status === 'completed' ? 'Completed' :
                         req.status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                      {req.urgent && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Urgent
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Requested By:</strong> {req.requestedBy}</p>
                        <p><strong>Role Type:</strong> {req.roleType}</p>
                        <p><strong>Organisational Unit:</strong> {req.organisationalUnit}</p>
                      </div>
                      <div>
                        <p><strong>Requested At:</strong> {formatDate(req.requestedAt)}</p>
                        {req.updatedBy && (
                          <p><strong>Last Updated By:</strong> {req.updatedBy}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Reason:</strong> {req.reason}
                    </p>
                    {req.notes && (
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Notes:</strong> {req.notes}
                      </p>
                    )}
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                        className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md hover:bg-green-200 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleUpdateRequestStatus(req.id, 'completed')}
                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 transition-colors"
                      >
                        Mark Completed
                      </button>
                      <button
                        onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                        className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-md hover:bg-red-200 transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  
                  {req.status === 'approved' && (
                    <div className="ml-4 text-sm text-green-600">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Role request approved
                      </div>
                      {req.approvedAt && (
                        <div className="mt-1 text-xs text-gray-500">
                          Approved: {formatDate(req.approvedAt)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {req.status === 'completed' && (
                    <div className="ml-4 text-sm text-blue-600">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 mr-1" />
                        Role assignment completed
                      </div>
                    </div>
                  )}
                  
                  {req.status === 'rejected' && (
                    <div className="ml-4 text-sm text-red-600">
                      <div className="flex items-center">
                        <XCircle className="h-4 w-4 mr-1" />
                        Request rejected
                      </div>
                      {req.rejectedAt && (
                        <div className="mt-1 text-xs text-gray-500">
                          Rejected: {formatDate(req.rejectedAt)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-8 text-center">
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending role requests</h3>
            <p className="mt-1 text-sm text-gray-500">
              All role requests have been processed or no requests have been submitted yet.
            </p>
          </div>
        </div>
      )}

      {/* Role Request Form Modal */}
      {showRoleRequestForm && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Request Administrative Role</h2>
                <button
                  onClick={() => setShowRoleRequestForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            
            {loadingFormData ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading form data...</p>
              </div>
            ) : (
              <form onSubmit={handleRoleRequestSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Users className="h-4 w-4 inline mr-1" />
                      Requested For (Existing User)
                    </label>
                    <select
                      value={roleRequest.requestedFor}
                      onChange={(e) => setRoleRequest({ ...roleRequest, requestedFor: e.target.value })}
                      className="input-field"
                      required
                      disabled={loadingFormData || allUsers.length === 0}
                    >
                      <option value="">-- Select User --</option>
                      {allUsers.map(user => (
                        <option key={user.id} value={user.primaryEmail}>
                          {user.name?.fullName || `${user.name?.givenName} ${user.name?.familyName}`} ({user.primaryEmail})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Select an existing user to assign the role to</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Requested By
                    </label>
                    <input
                      type="email"
                      value={roleRequest.requestedBy}
                      onChange={(e) => setRoleRequest({ ...roleRequest, requestedBy: e.target.value })}
                      className="input-field bg-gray-50"
                      placeholder={user?.email || "Your email"}
                      required
                      readOnly
                      title="This field is auto-populated with your email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Type
                  </label>
                  <select
                    value={roleRequest.roleType}
                    onChange={(e) => setRoleRequest({ ...roleRequest, roleType: e.target.value })}
                    className="input-field"
                    required
                    disabled={loadingFormData}
                  >
                    <option value="">Select a role type</option>
                    {roleTypes.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-1" />
                    Organisational Unit
                  </label>
                  <select
                    value={roleRequest.organisationalUnit}
                    onChange={(e) => handleOUChange(e.target.value)}
                    className="input-field"
                    required
                    disabled={loadingFormData}
                  >
                    <option value="">-- Select Organisational Unit --</option>
                    {/* Primary OUs (Customer Root OUs) - shown first */}
                    {primaryOUs.length > 0 && (
                      <optgroup label="Customer Root OUs (Primary)">
                        {primaryOUs.map(ou => (
                          <option key={ou.queueId} value={ou.queuePath || ou.queueId}>
                            {ou.queueName} ({ou.queuePath || ou.queueId}) - Customer Root
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {/* Regular OUs */}
                    {organisationalUnits.length > 0 && (
                      <optgroup label="Organisational Units">
                        {organisationalUnits.map(ou => (
                          <option key={ou.id} value={ou.path || ou.id}>
                            {ou.name} ({ou.path || ou.id})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select the OU where the role will be assigned. Customer Root OUs are the primary organisational units for each customer.
                  </p>
                </div>

                {roleRequest.organisationalUnit && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Mail className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-2">Approval Email Recipients</p>
                        {loadingApprovers ? (
                          <div className="flex items-center text-xs text-blue-700">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                            Loading approvers...
                          </div>
                        ) : ouApprovers.length > 0 ? (
                          <div>
                            <p className="text-xs text-blue-700 mb-2">
                              Approval emails will be sent to the following approvers for Queue: <strong>{queueInfo?.queueName || 'N/A'}</strong>
                            </p>
                            <select
                              className="input-field bg-white text-sm"
                              size={Math.min(ouApprovers.length, 5)}
                              disabled
                            >
                              {ouApprovers.map((email, index) => (
                                <option key={index} value={email}>
                                  {email}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-blue-600 mt-1 italic">
                              {ouApprovers.length} approver{ouApprovers.length !== 1 ? 's' : ''} will receive the approval email
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-blue-700">
                            No specific approvers found for this OU. Approval emails will be sent to Entwined administrators.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Request
                </label>
                <textarea
                  value={roleRequest.reason}
                  onChange={(e) => setRoleRequest({ ...roleRequest, reason: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder="Please explain why this role is needed..."
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="urgent"
                  checked={roleRequest.urgent}
                  onChange={(e) => setRoleRequest({ ...roleRequest, urgent: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="urgent" className="ml-2 text-sm text-gray-700">
                  This is an urgent request
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRoleRequestForm(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleDelegation; 